import { useState } from "react";
import { Box, Chip, Collapse, Divider, Skeleton, Stack, Tooltip, Typography } from "@material-ui/core";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import LocalFireDepartmentRoundedIcon from "@material-ui/icons/LocalFireDepartmentRounded";
import Label from "components/Label";
import CrmMemberAvatar from "../../org/CrmMemberAvatar";
import { exactHebrew, relativeHebrew } from "../../crmFormat";
import { timelineTypeTitle } from "../../../contacts/detail/tabs/timeline/TimelineItemRow";
import type { AgencyHolderView, AgencyTimelineEntry } from "./agencyPersonModel";
import type { ContactAgencyState } from "./useContactAgency";

/**
 * THIS PERSON, ACROSS THE AGENCY — the סיכום and שמור אצל pair that sits above the ציר זמן.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * One block, two hosts, and that is the point
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * It renders at the top of `TimelineSurface`, which is BOTH the contact page's ציר זמן tab
 * and the body of the agency-person panel. Before this it existed only in the panel, drawn
 * against a bespoke mini-feed; the panel now shows the real timeline, so the two facts the
 * panel was built to carry — who got there first, and who else holds this person — had to
 * move up into the shared component rather than be lost with the feed they were attached
 * to.
 *
 * ── EXACT TIMESTAMPS, which is why the server changed ────────────────────────────
 *
 * The inline strip this lineage started as printed `נוצר לפני 3 חוד׳ · עודכן לפני חודשיים`.
 * Relative strings cannot answer the question this block exists for — comparing
 * "לפני 3 חוד׳" against "לפני 4 חוד׳" down a column of colleagues is exactly how you fail to
 * see who got there first — so every timestamp here is absolute, with the relative form
 * demoted to the tooltip.
 *
 * That precision had to be EARNED. Measured on the mirror (2026-08-23): `doc.updatedTime`
 * misses real edits (it tracked 4 of 7 audited realtor writes), while Firestore's own commit
 * version is pinned to a mass Cloud-Function pass — 86.6% of the corpus carries it in one
 * month. Neither is a truthful "last change" alone, so the server sends the LATER of the two
 * and this block labels it `שינוי אחרון` — never `פנייה אחרונה`, which would claim a human
 * did something.
 *
 * ── WHAT IT DOES NOT CLAIM ──────────────────────────────────────────────────────
 *
 * "Who last TALKED to the lead" was asked for and does not exist: there is no per-holder
 * call or message timestamp anywhere, and the CRM mirror has no activity table at all. So
 * the third line is `פעילות אחרונה` — the newest real event on the feed below, named by
 * TYPE with the colleague it belongs to. It never uses the verb "שוחח", and it excludes
 * three things a naive reading would include: future meetings (they are timestamped with
 * their scheduled start), bulk sends (a campaign is not a conversation), and the synthetic
 * "created" rows (they are this block, restated).
 *
 * ── PERMISSIONS ─────────────────────────────────────────────────────────────────
 *
 * Nothing here decides anything. Every holder field was already redacted to the caller's
 * `pool` by `buildHolderViews` — the single enforcement point, backed by a test that feeds
 * a basic build a holder carrying every full-only field and asserts none survives — and the
 * server did it first by not SELECTING those columns at all. The one rule this file must
 * keep is to render `AgencyHolderView`, never a raw `CrmContactHolder`.
 */

export type ContactAgencySectionsProps = {
  agency: ContactAgencyState;
  /** From the merged feed below, so the summary and the first row can never disagree. */
  lastActivity: AgencyTimelineEntry | null;
  /** The feed is still loading; the activity line shows a placeholder rather than "none". */
  activityLoading: boolean;
  /**
   * Open on the panel, shut on the contact page.
   *
   * The panel IS this block — a reader who opened it did so to see the list. The tab is
   * about the person's history, where a roster of colleagues above the feed is context: it
   * announces itself with the count and the faces, and unfolds if asked.
   */
  defaultHoldersOpen?: boolean;
};

export function ContactAgencySections({
  agency,
  lastActivity,
  activityLoading,
  defaultHoldersOpen = false
}: ContactAgencySectionsProps): JSX.Element {
  return (
    /* Deliberately the SAME furniture as the filters bar directly below it — neutral fill,
       hairline border, the same radius. A card here would read as a second document on the
       page; this reads as the header of the one that is already there. */
    <Box
      sx={{
        mb: 2,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: "background.neutral",
        border: (theme) => `1px solid ${theme.palette.divider}`
      }}
    >
      <SummarySection agency={agency} lastActivity={lastActivity} activityLoading={activityLoading} />
      <Divider sx={{ my: 1.25 }} />
      <HoldersSection agency={agency} defaultOpen={defaultHoldersOpen} />
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// סיכום
// ───────────────────────────────────────────────────────────────────────────────

/** A caption/value pair. The caption is Hebrew prose and always RTL; the value picks its own. */
function SummaryRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Box sx={{ typography: "body2", fontWeight: 500 }}>{children}</Box>
    </Box>
  );
}

function SummarySection({
  agency,
  lastActivity,
  activityLoading
}: {
  agency: ContactAgencyState;
  lastActivity: AgencyTimelineEntry | null;
  activityLoading: boolean;
}): JSX.Element {
  const { summary } = agency;

  return (
    <Box>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        סיכום
      </Typography>
      <Stack spacing={1.25} sx={{ mt: 0.5 }}>
        <SummaryRow label="נקלט ראשון אצל">
          {summary.first ? (
            <>
              {summary.first.ownerName} · <Stamp iso={summary.first.createdTime} />
              {/* ⚠️ The cap keeps the 20 most recently TOUCHED copies — the ordering LEAST
                  likely to contain the earliest — so above 20 holders this is a claim about
                  what is on screen, and it has to say so rather than assert the agency. */}
              {summary.firstIsAmongShownOnly && (
                <Typography variant="caption" sx={{ display: "block", color: "text.disabled" }}>
                  מבין {agency.holders.length} המתווכים המוצגים
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">
              לא ידוע
            </Typography>
          )}
        </SummaryRow>

        {/* `pool: "full"` ONLY, and ABSENT rather than placeholdered at basic. Saying "a
            source is hidden" still discloses that one exists, which is precisely the
            commercial fact about the colleague the server withholds. */}
        {agency.pool === "full" && summary.originLabel && (
          <SummaryRow label="מקור הליד">{summary.originLabel}</SummaryRow>
        )}

        <SummaryRow label="פעילות אחרונה">
          {activityLoading && !lastActivity ? (
            <Skeleton variant="text" width={180} />
          ) : lastActivity ? (
            <>
              {timelineTypeTitle(lastActivity.actionType)} · {lastActivity.user.userName} ·{" "}
              <Stamp iso={lastActivity.time ? lastActivity.time.toISOString() : null} />
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">
              אין פעילות מתועדת
            </Typography>
          )}
        </SummaryRow>
      </Stack>
    </Box>
  );
}

/**
 * An exact moment, with the relative form on the tooltip.
 *
 * ⚠️ `dir="ltr"` ON AN INLINE-BLOCK. The string is digits, dots and a colon — no strong
 * directional character at all — so under RTL the bidi algorithm would reorder the
 * separators around the numbers. The ATTRIBUTE rather than a `direction` in `sx`, because
 * stylis-plugin-rtl mirrors the CSS property and ships the opposite; and inline-block so it
 * shrink-wraps rather than taking a full-width box whose `text-align: start` would then
 * resolve against its own LTR direction and pin it to the far edge.
 */
function Stamp({ iso }: { iso: string | null }): JSX.Element | null {
  if (!iso) return null;
  return (
    <Tooltip title={relativeHebrew(iso)}>
      <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
        {exactHebrew(iso)}
      </Box>
    </Tooltip>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// שמור אצל
// ───────────────────────────────────────────────────────────────────────────────

/** How many faces the collapsed header previews before it starts counting instead. */
const FACE_PREVIEW = 5;

function HoldersSection({
  agency,
  defaultOpen
}: {
  agency: ContactAgencyState;
  defaultOpen: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const shown = agency.holders.length;

  return (
    <Box>
      {/*
        A real <button>, not a Box with an onClick: this is the one interactive control in
        the block, and keyboard access + the announced expanded state come free from the
        element rather than from three ARIA attributes that have to be kept in sync.
        `component="button"` on a Box would give the element without the button reset, and a
        <button> inherits neither the page font nor `text-align: start`.
      */}
      <Stack
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          width: "100%",
          p: 0,
          border: 0,
          bgcolor: "transparent",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          textAlign: "start"
        }}
      >
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          שמור אצל
        </Typography>
        {/* `pointerEvents: none` on everything decorative inside the button: a Chip sets
            `cursor: default` on itself, so without this the pointer changes back to an arrow
            over the middle of a control that is entirely clickable. */}
        <Chip
          size="small"
          label={agency.count}
          sx={{ height: 18, fontSize: 11, fontWeight: 700, pointerEvents: "none" }}
        />

        {/* The faces stay visible while collapsed — that is what makes a shut section
            still answer "how many of us, and who". */}
        <Stack direction="row" spacing={0.25} sx={{ minWidth: 0, flexGrow: 1, pointerEvents: "none" }}>
          {!open &&
            agency.holders.slice(0, FACE_PREVIEW).map((holder) => (
              <CrmMemberAvatar
                key={holder.ownerUserId}
                size={20}
                name={holder.ownerName ?? holder.ownerUserId}
                avatarUrl={holder.ownerAvatarUrl ?? ""}
              />
            ))}
          {!open && agency.count > FACE_PREVIEW && (
            <Typography variant="caption" sx={{ color: "text.secondary", alignSelf: "center" }}>
              +{agency.count - FACE_PREVIEW}
            </Typography>
          )}
        </Stack>

        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 20,
            flexShrink: 0,
            color: "text.secondary",
            // A rotation, not two icons: the arrow animates from one state to the other, so
            // the control reads as the same object throughout.
            transform: open ? "rotate(180deg)" : "none",
            transition: (theme) => theme.transitions.create("transform", { duration: 150 })
          }}
        />
      </Stack>

      <Collapse in={open} unmountOnExit>
        {/* ⚠️ `count`, not `holders.length`. The strip this replaces printed the CAPPED array
            length, so a person held by 25 agents read "20" under a badge that correctly said
            "+24". The truncation gets its own line rather than being hidden in the number. */}
        {shown < agency.count && (
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.disabled" }}>
            מוצגים {shown} מתוך {agency.count}
          </Typography>
        )}

        <Stack sx={{ mt: 1 }} divider={<Divider sx={{ borderStyle: "dashed" }} />} spacing={1.25}>
          {agency.holders.map((holder) => (
            <HolderRow key={holder.ownerUserId} holder={holder} />
          ))}
        </Stack>

        {agency.pool === "basic" && (
          <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.disabled" }}>
            מוצגים פרטים בסיסיים בלבד — הרשאת הצפייה בכרטיס של מתווך אחר נקבעת בהגדרות המשרד.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}

function HolderRow({ holder }: { holder: AgencyHolderView }): JSX.Element {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <CrmMemberAvatar
          size={28}
          name={holder.ownerName ?? holder.ownerUserId}
          avatarUrl={holder.ownerAvatarUrl ?? ""}
        />
        <Typography variant="subtitle2" noWrap sx={{ minWidth: 0, flexGrow: 1 }}>
          {holder.ownerName ?? holder.ownerUserId}
        </Typography>
        {holder.isMine && <Chip size="small" label="שלך" sx={{ height: 20, fontSize: 11 }} />}
        {/* The claim the summary makes, shown on the row it is about — so it can be checked
            rather than taken on trust. */}
        {holder.isFirst && <Chip size="small" color="info" label="ראשון" sx={{ height: 20, fontSize: 11 }} />}
        {holder.isHot && <LocalFireDepartmentRoundedIcon sx={{ fontSize: 16, color: "error.main" }} />}
        {holder.statusLabel && <Label color="info" variant="ghost">{holder.statusLabel}</Label>}
      </Stack>

      {/* TWO LINES, not one dot-joined line. An exact timestamp already contains a "·"
          between its date and its time, so joining the facts with the same separator made
          the row unreadable — and a block has the vertical room the inline strip did not. */}
      <Stack sx={{ mt: 0.5, pl: 4.5 }}>
        {holder.name && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            בשם {holder.name}
          </Typography>
        )}
        {holder.createdTime && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            נוצר <Stamp iso={holder.createdTime} />
          </Typography>
        )}
        {holder.lastChangeTime && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {/* NEVER "פנייה אחרונה" — this moves on writes no human made. */}
            שינוי אחרון <Stamp iso={holder.lastChangeTime} />
          </Typography>
        )}
        {holder.criteria && holder.criteria.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {holder.criteria.join(" · ")}
          </Typography>
        )}
        {holder.tags && holder.tags.length > 0 && (
          <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
            {holder.tags.slice(0, 6).map((tag) => (
              <Label key={tag} color="default" variant="ghost">
                {tag}
              </Label>
            ))}
          </Stack>
        )}
        {holder.notes && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.25,
              fontStyle: "italic",
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}
          >
            {holder.notes}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
